import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { NavigateNext, Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_TASKS_WITH_FIELDS } from '../../../admin/tasks-import/get-tasks-with-fields.gql';
import { TaskFieldSelector } from './TaskFieldSelector';
import { TaskFilterConfigurator } from './TaskFilterConfigurator';

interface TaskFieldSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (params: TaskFieldSearchParams) => void;
  initialFilters?: TaskFieldSearchParams;
}

export interface FieldFilterConfig {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  taskId: string;
  taskTitle: string;
  valueFilter?: ValueFilterConfig;
  completionFilter?: CompletionFilterConfig;
}

interface TaskFilterConfig {
  taskId: string;
  taskTitle: string;
  completionFilter?: CompletionFilterConfig;
  fields: FieldFilterConfig[];
}

interface CompletionFilterConfig {
  type: 'completed' | 'not_completed' | 'completed_on' | 'completed_before' | 'completed_after' | 'completed_between';
  date?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface ValueFilterConfig {
  type:
    | 'contains'
    | 'equals'
    | 'starts_with'
    | 'greater_than'
    | 'less_than'
    | 'equals_number'
    | 'on_date'
    | 'before_date'
    | 'after_date'
    | 'between_dates'
    | 'has_attachment'
    | 'option_selected';
  value?: string | number;
  dateValue?: string;
  dateFrom?: string;
  dateTo?: string;
  selectedOptions?: string[]; // For dropdown/checkbox/multiple choice
  optionLogic?: 'AND' | 'OR'; // For multiple option selections
}

export interface TaskFieldSearchParams {
  filters: FieldFilterConfig[];
  filterLogic: 'AND' | 'OR';
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogTitle: {
      paddingBottom: '8px',
    },
    stepIndicator: {
      fontSize: '12px',
      color: '#6b7280',
      marginBottom: '4px',
      textTransform: 'uppercase',
      fontWeight: 500,
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 600,
      marginBottom: '16px',
    },
    description: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '24px',
    },
    dialogContent: {
      paddingTop: '24px',
      paddingBottom: '4em',
    },
    dialogActions: {
      padding: '16px 24px',
      borderTop: '1px solid #e5e7eb',
    },
    taskBox: {
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
    },
    fieldBox: {
      marginLeft: '48px',
      marginRight: '20px',
      paddingLeft: '16px',
      borderLeft: '2px solid #e5e7eb',
      marginTop: '8px',
    },
    filterConfigBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    filterLogicBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    fieldTitle: {
      fontSize: '15px',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '4px',
    },
    fieldSubtitle: {
      fontSize: '10px',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
    },
    sectionLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '8px',
      marginTop: '12px',
    },
    checkboxLabel: {
      fontSize: '13px',
      fontWeight: 400,
      color: '#4b5563',
    },
    formControl: {
      '& .MuiInputLabel-root': {
        fontSize: '11px',
        color: '#6b7280',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontSize: '12px',
      },
      '& .MuiOutlinedInput-root': {
        fontSize: '13px',
        '& fieldset': {
          borderColor: '#e5e7eb',
        },
        '&:hover fieldset': {
          borderColor: '#7047EB',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#7047EB',
        },
      },
      '& .MuiSelect-select': {
        fontSize: '13px !important',
        fontWeight: '400 !important',
        padding: '8px 14px !important',
        lineHeight: '1.3 !important',
      },
      '& .MuiInputBase-input': {
        fontSize: '13px !important',
        fontWeight: '400 !important',
        padding: '6px 14px !important',
        lineHeight: '1.3 !important',
      },
      '& .MuiMenuItem-root': {
        fontSize: '13px',
      },
    },
    menuItem: {
      fontSize: '13px',
      padding: '8px 16px',
      '&.Mui-selected': {
        backgroundColor: '#f5f3ff',
      },
      '&.Mui-selected:hover': {
        backgroundColor: '#ede9fe',
      },
      '&:hover': {
        backgroundColor: '#f9fafb',
      },
    },
  })
);

export const TaskFieldSearchDialog = (props: TaskFieldSearchDialogProps) => {
  const { open, onClose, onSearch, initialFilters } = props;
  const { isMobile } = useCampfireTheme();
  const classes = useStyles();

  // Step management
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1: Task/Field selection
  const [selectedFields, setSelectedFields] = useState<Map<string, FieldFilterConfig>>(new Map());

  // Step 2: Filter configuration
  const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>('AND');

  // Task-level completion filters (stored separately by taskId)
  const [taskCompletionFilters, setTaskCompletionFilters] = useState<Map<string, CompletionFilterConfig>>(new Map());

  // Initialize from initialFilters when dialog opens with existing filters
  React.useEffect(() => {
    if (open && initialFilters) {
      // Populate selectedFields from initialFilters
      const fieldsMap = new Map<string, FieldFilterConfig>();
      const completionFiltersMap = new Map<string, CompletionFilterConfig>();

      initialFilters.filters.forEach((filter) => {
        fieldsMap.set(filter.fieldId, {
          fieldId: filter.fieldId,
          fieldName: filter.fieldName,
          fieldType: filter.fieldType,
          taskId: filter.taskId,
          taskTitle: filter.taskTitle,
          valueFilter: filter.valueFilter,
          completionFilter: filter.completionFilter,
        });

        // Extract task completion filters
        if (filter.completionFilter) {
          completionFiltersMap.set(filter.taskId, filter.completionFilter);
        }
      });

      setSelectedFields(fieldsMap);
      setTaskCompletionFilters(completionFiltersMap);
      setFilterLogic(initialFilters.filterLogic);
      // Skip to step 2 when editing existing filters
      setCurrentStep(2);
    }
  }, [open, initialFilters]);

  // Query for tasks with fields
  const { data: tasksData } = useCampfireQuery(GET_TASKS_WITH_FIELDS, {});

  const tasks = tasksData?.vm?.tasks || [];

  const handleToggleField = (task: any, field: any) => {
    const fieldKey = field.taskFieldId;
    setSelectedFields((prev) => {
      const next = new Map(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.set(fieldKey, {
          fieldId: field.taskFieldId,
          fieldName: field.name,
          fieldType: field.__typename,
          taskId: task.taskId,
          taskTitle: task.title,
        });
      }
      return next;
    });
  };

  const handleToggleAllTaskFields = (task: any, fields: any[]) => {
    const allSelected = fields.every((field) => selectedFields.has(field.taskFieldId));

    setSelectedFields((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        // Deselect all fields from this task
        fields.forEach((field) => {
          next.delete(field.taskFieldId);
        });
      } else {
        // Select all fields from this task
        fields.forEach((field) => {
          next.set(field.taskFieldId, {
            fieldId: field.taskFieldId,
            fieldName: field.name,
            fieldType: field.__typename,
            taskId: task.taskId,
            taskTitle: task.title,
          });
        });
      }
      return next;
    });
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedFields.size > 0) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSearch = () => {
    // Helper function to convert GraphQL typename to backend field type
    const getBackendFieldType = (typename: string): string => {
      const typeMap: Record<string, string> = {
        VOLUNTEER_ShortTextTaskFieldType: 'ShortText',
        VOLUNTEER_LongTextTaskFieldType: 'LongText',
        VOLUNTEER_EmailTaskFieldType: 'Email',
        VOLUNTEER_PhoneTaskFieldType: 'Phone',
        VOLUNTEER_NumberTaskFieldType: 'Number',
        VOLUNTEER_DateTaskFieldType: 'Date',
        VOLUNTEER_DropdownTaskFieldType: 'Dropdown',
        VOLUNTEER_MultipleChoiceTaskFieldType: 'MultipleChoice',
        VOLUNTEER_CheckboxTaskFieldType: 'Checkbox',
        VOLUNTEER_AttachmentTaskFieldType: 'Attachment',
        VOLUNTEER_QuizTaskFieldType: 'Quiz',
      };
      return typeMap[typename] || typename;
    };

    // Helper function to convert frontend filter type to backend filter type
    const getBackendFilterType = (frontendType: string): string => {
      const typeMap: Record<string, string> = {
        contains: 'contains',
        equals: 'equals',
        startsWith: 'startsWith',
        greaterThan: 'greaterThan',
        lessThan: 'lessThan',
        equalsNumber: 'equals',
        hasAttachment: 'hasAttachment',
        optionSelected: 'optionSelected',
      };
      return typeMap[frontendType] || frontendType;
    };

    // Helper function to convert completion filter type
    const getBackendCompletionFilterType = (frontendType: string): string => {
      const typeMap: Record<string, string> = {
        completed: 'completed',
        notCompleted: 'notCompleted',
        completedOn: 'completedOn',
        completedBefore: 'completedBefore',
        completedAfter: 'completedAfter',
        completedBetween: 'completedBetween',
      };
      return typeMap[frontendType] || frontendType;
    };

    // Build filters with completion filters attached
    const allFields = Array.from(selectedFields.values());

    // Attach task completion filters to fields
    const fieldsWithCompletion = allFields.map((field) => {
      const completionFilter = taskCompletionFilters.get(field.taskId);
      return {
        ...field,
        completionFilter,
      };
    });

    // Filter to only include fields/tasks with actual criteria
    const fieldsWithCriteria = fieldsWithCompletion.filter((field) => {
      // Has value filter criteria?
      let hasValueFilter = false;
      if (field.valueFilter) {
        const { valueFilter } = field;
        if (valueFilter.type === 'has_attachment') hasValueFilter = true;
        if (valueFilter.type === 'option_selected' && valueFilter.selectedOptions?.length) hasValueFilter = true;
        if (valueFilter.value !== undefined && valueFilter.value !== '') hasValueFilter = true;
        if (valueFilter.dateValue) hasValueFilter = true;
        if (valueFilter.dateFrom || valueFilter.dateTo) hasValueFilter = true;
      }

      // Has completion filter?
      const hasCompletionFilter = !!field.completionFilter;

      // Include if it has either value filter OR completion filter
      return hasValueFilter || hasCompletionFilter;
    });

    // Transform filters to backend format
    const filters = fieldsWithCriteria.map((field) => {
      const backendFilter: any = {
        taskId: field.taskId,
        fieldId: field.fieldId,
        // Include these for frontend column display
        fieldName: field.fieldName,
        taskTitle: field.taskTitle,
        fieldType: field.fieldType,
      };

      // Transform value filter if present
      if (field.valueFilter) {
        backendFilter.valueFilter = {
          fieldType: getBackendFieldType(field.fieldType),
          filterType: getBackendFilterType(field.valueFilter.type),
          ...field.valueFilter,
        };
        // Remove the frontend 'type' field as we've replaced it with 'filterType'
        delete backendFilter.valueFilter.type;
      }

      // Transform completion filter if present
      if (field.completionFilter) {
        backendFilter.completionFilter = {
          filterType: getBackendCompletionFilterType(field.completionFilter.type),
          ...field.completionFilter,
        };
        // Remove the frontend 'type' field as we've replaced it with 'filterType'
        delete backendFilter.completionFilter.type;
      }

      return backendFilter;
    });

    onSearch({
      filters,
      filterLogic,
    });
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedFields(new Map());
    setFilterLogic('AND');
    setTaskCompletionFilters(new Map());
    onClose();
  };

  const updateTaskCompletionFilter = (taskId: string, completionFilter?: CompletionFilterConfig) => {
    setTaskCompletionFilters((prev) => {
      const next = new Map(prev);
      if (completionFilter) {
        next.set(taskId, completionFilter);
      } else {
        next.delete(taskId);
      }
      return next;
    });
  };

  const updateFieldFilter = (fieldId: string, updates: Partial<FieldFilterConfig>) => {
    setSelectedFields((prev) => {
      const next = new Map(prev);
      const existing = next.get(fieldId);
      if (existing) {
        next.set(fieldId, { ...existing, ...updates });
      }
      return next;
    });
  };

  const canProceed = currentStep === 1 ? selectedFields.size > 0 : true;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth='md' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography className={classes.stepIndicator}>Step {currentStep} of 2</Typography>
            <Typography variant='h6'>{currentStep === 1 ? 'Select Tasks' : 'Configure Filters'}</Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {currentStep === 1 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Select the task fields you want to filter by. Expand tasks to view and select their fields. In the next
                step, you&apos;ll configure how to filter each field.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TaskFieldSelector
                tasks={tasks}
                selectedFields={selectedFields}
                onToggleField={handleToggleField}
                onToggleAllTaskFields={handleToggleAllTaskFields}
              />
            </Grid>
          </Grid>
        )}

        {currentStep === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Configure how you want to filter each selected task and field. You can filter by task completion, field
                values, or both.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.filterLogicBox}>
                <Typography className={classes.fieldTitle}>Filter Logic</Typography>
                <Typography className={classes.description} style={{ marginBottom: '12px' }}>
                  When filtering by multiple fields, should volunteers match ALL or ANY of the conditions?
                </Typography>
                <RadioGroup row value={filterLogic} onChange={(e) => setFilterLogic(e.target.value as 'AND' | 'OR')}>
                  <FormControlLabel
                    value='AND'
                    control={
                      <Radio
                        size='small'
                        style={{ padding: 0, marginRight: '6px', color: filterLogic === 'AND' ? '#7047EB' : undefined }}
                      />
                    }
                    label={<Typography className={classes.checkboxLabel}>Match ALL (AND)</Typography>}
                    style={{ marginLeft: 0, marginRight: '24px' }}
                  />
                  <FormControlLabel
                    value='OR'
                    control={
                      <Radio
                        size='small'
                        style={{ padding: 0, marginRight: '6px', color: filterLogic === 'OR' ? '#7047EB' : undefined }}
                      />
                    }
                    label={<Typography className={classes.checkboxLabel}>Match ANY (OR)</Typography>}
                    style={{ marginLeft: 0 }}
                  />
                </RadioGroup>
              </Box>
            </Grid>

            {(() => {
              // Group fields by task
              const taskGroups = new Map<string, TaskFilterConfig>();
              selectedFields.forEach((fieldConfig) => {
                if (!taskGroups.has(fieldConfig.taskId)) {
                  taskGroups.set(fieldConfig.taskId, {
                    taskId: fieldConfig.taskId,
                    taskTitle: fieldConfig.taskTitle,
                    completionFilter: taskCompletionFilters.get(fieldConfig.taskId),
                    fields: [],
                  });
                }
                taskGroups.get(fieldConfig.taskId)!.fields.push(fieldConfig);
              });

              return Array.from(taskGroups.values()).map((taskConfig) => (
                <Grid item xs={12} key={taskConfig.taskId}>
                  <TaskFilterConfigurator
                    taskConfig={taskConfig}
                    tasks={tasks}
                    onUpdateTask={(updates) => {
                      updateTaskCompletionFilter(taskConfig.taskId, updates.completionFilter);
                    }}
                    onUpdateField={(fieldId, updates) => updateFieldFilter(fieldId, updates)}
                  />
                </Grid>
              ));
            })()}
          </Grid>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {currentStep === 2 && (
          <TabletButton variant='text' onClick={handleBack} style={{ marginRight: 'auto', textTransform: 'none' }}>
            Back
          </TabletButton>
        )}

        <TabletButton
          variant='text'
          color='error'
          onClick={handleClose}
          style={{ marginRight: 8, textTransform: 'none' }}
        >
          Cancel
        </TabletButton>

        {currentStep === 1 && (
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleNext}
            disabled={!canProceed}
            endIcon={<NavigateNext />}
            style={{ textTransform: 'none' }}
          >
            Next
          </TabletButton>
        )}

        {currentStep === 2 && (
          <TabletButton variant='contained' color='primary' onClick={handleSearch} style={{ textTransform: 'none' }}>
            Search
          </TabletButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
